import {
  faBed,
  faBus,
  faGlobe,
  faPlane,
  faShieldAlt,
  faStar,
  faUtensils,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import {
  ApiComponent,
  ApiComponentType,
  ApiExtraService,
  ApiFlightFare,
  ApiFlightItem,
  ApiFlightSegment,
  ApiHotel,
  ApiImage,
  ApiInsurance,
  ApiItem,
  ApiItemType,
  ApiMedia,
  ApiPackageDetails,
  ApiPackageModel,
  ApiPrice,
  ApiTransfer,
  ApiTravelDateCandidate
} from '@ibe/api';
import { createUniqueId } from '@ibe/services';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import { ItemTypesEnum } from '../../../../Models/ItemTypesEnum';
import PackageCustomizerColumn from './PackageCustomizerColumn';
import PackageCustomizerRow from './PackageCustomizerRow';

abstract class PackageCustomizerUtil {
  public static shiftComponentIndex(
    arr: ApiComponent[],
    currentIndex: number,
    currentComponent: ApiComponent,
    firstIndexCheck: boolean,
    findGivenIndex: (arr: ApiComponent[]) => number
  ): number {
    arr.splice(currentIndex, 1);
    let firstIndexAfterGivenComponent;
    if (firstIndexCheck) {
      firstIndexAfterGivenComponent = 0;
    } else {
      firstIndexAfterGivenComponent = findGivenIndex(arr);
      if (firstIndexAfterGivenComponent === -1) firstIndexAfterGivenComponent = 0;
    }
    arr.splice(firstIndexAfterGivenComponent, 0, currentComponent);

    return firstIndexAfterGivenComponent;
  }

  public static createRows(
    numberOfColumns: number,
    components: ApiComponent[],
    packageStartDate: string
  ): PackageCustomizerRow[] {
    let rows: PackageCustomizerRow[] = [];
    this.createEmptyRow(numberOfColumns, rows);
    const packageStart = dayjs(packageStartDate);

    const sortedComponents = [...components];

    const mainComponentIndex = sortedComponents.findIndex(
      item => item.componentType === ApiComponentType.MAIN
    );
    if (mainComponentIndex !== -1) {
      const mainComponent = sortedComponents[mainComponentIndex];
      sortedComponents.splice(mainComponentIndex, 1);
      sortedComponents.unshift(mainComponent);
    }

    sortedComponents.forEach((item, idx, arr) => {
      if (item.itemType === ApiItemType.FLIGHT) {
        const firstIndexAfterMain = this.shiftComponentIndex(
          arr,
          idx,
          item,
          arr[0].componentType !== ApiComponentType.MAIN,
          (array: ApiComponent[]) =>
            array.findIndex((elem: ApiComponent) => elem.componentType !== ApiComponentType.MAIN)
        );
        const related = arr.findIndex(elem => elem.id === item.relatedComponentId);
        if (related !== -1) {
          const relatedComponent = arr[related];
          arr.splice(related, 1);
          arr.splice(firstIndexAfterMain, 0, relatedComponent);
        }
      } else if (item.componentType === ApiComponentType.REQUIRED) {
        this.shiftComponentIndex(
          arr,
          idx,
          item,
          arr[0].itemType !== ApiItemType.FLIGHT && arr[0].componentType !== ApiComponentType.MAIN,
          (array: ApiComponent[]) =>
            array.findIndex(
              (elem: ApiComponent) =>
                elem.itemType !== ApiItemType.FLIGHT && elem.componentType !== ApiComponentType.MAIN
            )
        );
      }
    });

    sortedComponents.forEach(component => {
      const startDate = dayjs(component.startDate);
      const endDate = dayjs(component.endDate);
      let componentCols: number = endDate.diff(startDate, 'day') + 1;
      // hack as insurance is somehow longer than package
      if (componentCols > numberOfColumns) {
        componentCols = numberOfColumns;
      }
      const componentStartIndex: number = startDate.diff(packageStart, 'day');

      // check rows
      const rowForComponent = rows.find(row => {
        const candidates: PackageCustomizerColumn[] = row.columns.slice(
          componentStartIndex,
          componentStartIndex + componentCols
        );
        return candidates.every(col => {
          return col.component === undefined;
        });
      });

      if (rowForComponent) {
        const componentCol = this.createEmptyColumn();
        componentCol.component = component;
        if (component.relatedComponentId) {
          componentCol.relatedComponent = components.find(
            comp => component.relatedComponentId === comp.id
          );
        }
        let assignedCols = 0;
        let idx = componentStartIndex;
        while (assignedCols < componentCols) {
          rowForComponent.columns[idx] = componentCol;
          idx += 1;
          assignedCols += 1;
        }
      } else {
        this.createEmptyRow(numberOfColumns, rows);
        const componentCol = this.createEmptyColumn();
        componentCol.component = component;
        if (component.relatedComponentId) {
          componentCol.relatedComponent = components.find(
            comp => component.relatedComponentId === comp.id
          );
        }

        let assignedCols = 0;
        let idx = componentStartIndex;
        while (assignedCols < componentCols) {
          rows[rows.length - 1].columns[idx] = componentCol;
          idx += 1;
          assignedCols += 1;
        }
      }
    });
    // now calculate and group by colspan
    rows = this.stackColumns(rows);
    return rows;
  }

  public static getDates(startDate: string, endDate: string): string[] {
    const daysToAdd = 1;
    const dates: string[] = [];
    let start = dayjs(startDate);
    const end = dayjs(endDate);
    while (start.isBefore(end)) {
      dates.push(start.format('ddd MMM DD, YYYY '));
      start = start.add(daysToAdd, 'day');
    }
    dates.push(start.format('ddd MMM DD, YYYY '));
    return dates;
  }

  private static createEmptyColumn(): PackageCustomizerColumn {
    const column = new PackageCustomizerColumn();
    column.id = createUniqueId('row', 'key');
    return column;
  }

  private static createEmptyRow(numberOfColumns: number, rows: PackageCustomizerRow[]): void {
    const row = new PackageCustomizerRow();
    row.id = createUniqueId('row', 'key');
    row.columns = new Array(numberOfColumns).fill(
      new PackageCustomizerColumn(),
      0,
      numberOfColumns
    );
    rows.push(row);
  }

  private static stackColumns(rows: PackageCustomizerRow[]): PackageCustomizerRow[] {
    return rows.map(row => {
      const newColumns: PackageCustomizerColumn[] = [];
      let currentCol = this.createEmptyColumn();
      for (let i = 0; i < row.columns.length; i += 1) {
        // empty col
        if (!row.columns[i].component) {
          if (currentCol.component) {
            newColumns.push(currentCol);
            currentCol = this.createEmptyColumn();
          }
          newColumns.push(row.columns[i]);
        } else if (!currentCol.component) {
          currentCol.component = row.columns[i].component;
          currentCol.relatedComponent = row.columns[i].relatedComponent;
          currentCol.colSpan = 1;
        }
        // either current component enhanced or new start
        else if (currentCol.component.id === row.columns[i].component.id) {
          currentCol.colSpan += 1;
        } else {
          newColumns.push(currentCol);
          currentCol = this.createEmptyColumn();
          currentCol.component = row.columns[i].component;
          currentCol.relatedComponent = row.columns[i].relatedComponent;
          currentCol.colSpan = 1;
        }
      }
      if (currentCol.component) {
        newColumns.push(currentCol);
      }
      return { ...row, columns: newColumns };
    });
  }

  private static getProperArrayOfItems(component: ApiComponent): ApiItem[] | undefined {
    let arrayToBeSearched;

    if (component.selectedItems.length !== 0) {
      arrayToBeSearched = component.selectedItems;
    } else if (component.selectableItems.length !== 0) {
      arrayToBeSearched = component.selectableItems;
    }

    return arrayToBeSearched;
  }

  public static getFlightOriginFromComponent(component: ApiComponent): string | null {
    if (component.selectedItems) {
      const flightItem = (component.selectedItems as ApiFlightItem[]).find(
        item => item.segment && item.segment[0].origin
      );
      return flightItem?.segment[0].origin.code || null;
    }
    return null;
  }

  public static getFlightDestinationFromComponent(component: ApiComponent): string | null {
    if (component.selectedItems) {
      const flightItem = (component.selectedItems as ApiFlightItem[]).find(
        item => item.segment && item.segment[item.segment.length - 1].origin
      );
      return flightItem?.segment[flightItem.segment.length - 1].destination.code || null;
    }
    return null;
  }

  public static getDepartureDate(component: ApiComponent): string | null {
    const arrayToBeSearched = this.getProperArrayOfItems(component);

    const flightItem = (arrayToBeSearched as ApiFlightItem[]).find(
      item => item.segment && item.segment[0].departure
    );
    return flightItem?.segment[0].departure || null;
  }

  public static getArrivalDate(component: ApiComponent): string | null {
    const arrayToBeSearched = this.getProperArrayOfItems(component);

    const flightItem = (arrayToBeSearched as ApiFlightItem[]).find(
      item => item.segment && item.segment[item.segment.length - 1].arrival
    );
    return flightItem?.segment[flightItem.segment.length - 1].arrival || null;
  }

  public static getProperIconForGivenComponent(
    component: ApiComponent
  ): IconDefinition | undefined {
    if (component.itemType === ApiItemType.FLIGHT) {
      return faPlane;
    }
    if (component.itemType === ApiItemType.HOTEL) {
      return faBed;
    }
    if (component.itemType === ApiItemType.TOUR) {
      return faGlobe;
    }
    if (component.itemType === ApiItemType.INSURANCE) {
      return faShieldAlt;
    }
    if (component.itemType === ApiItemType.TRANSFER) {
      return faBus;
    }
    if (component.itemType === ApiItemType.EXTRA) {
      if (component.subItemType === ApiItemType.MEETANDGREET) {
        return faStar;
      }
      if (component.subItemType === ApiItemType.LOUNGEACCESS) {
        return faStar;
      }
      if (component.subItemType === ApiItemType.DINING) {
        return faUtensils;
      }
      return faStar;
    }
    return undefined;
  }

  public static getItemsTypeFromComponent(component: ApiComponent): string {
    if (component.itemType) {
      return component.itemType;
    }

    const arrayToBeSearched = this.getProperArrayOfItems(component);

    return arrayToBeSearched ? arrayToBeSearched[0].itemType : '';
  }

  public static getItemsSubTypeFromComponentItems(component: ApiComponent): string {
    return component.subItemType || component.itemType || '';
  }

  public static getTravelDateById(
    packageModel: ApiPackageModel | undefined,
    id: string | undefined
  ): ApiTravelDateCandidate | undefined {
    if (packageModel && id) {
      if (packageModel.travelDates) {
        return packageModel.travelDates.find(travelDate => travelDate.id === id);
      }
    }
    return undefined;
  }

  // todo: all of this only works with one unit
  public static getItemPrice(item: ApiItem): ApiPrice | undefined {
    if (item == null) {
      return undefined;
    }

    const type = item.type || null;

    if (type === ItemTypesEnum.FLIGHT) {
      const flightFares: ApiFlightFare[] = (item as ApiFlightItem).fare || [];
      return flightFares.length > 0 ? flightFares[0].price : undefined;
    }
    if (type === ItemTypesEnum.FLIGHT_FARE) {
      return item.price;
    }
    if (type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).units[0].price;
    }
    if (type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).rooms[0].roomrates[0].price;
    }
    if (type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).extraUnits[0].price;
    }
    if (type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).units[0].price;
    }
    if (type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).extraUnits[0].price;
    }

    return undefined;
  }

  public static getItemDeltaPrice(item: ApiItem, ref: ApiPrice | undefined): ApiPrice {
    const itemPrice = item.price;

    return this.getDeltaPrice(itemPrice, ref);
  }

  public static getDeltaPrice(itemPrice: ApiPrice, ref: ApiPrice | undefined): ApiPrice {
    if (ref && itemPrice) {
      const deltaPrice: ApiPrice = cloneDeep(itemPrice);

      if (deltaPrice) {
        deltaPrice.basePrice -= ref.basePrice;
        deltaPrice.finalPrice -= ref.finalPrice;

        return deltaPrice;
      }
    }

    return itemPrice;
  }

  public static hasMultiSelection(component: ApiComponent): boolean {
    return PackageCustomizerUtil.getItemsTypeFromComponent(component) === ItemTypesEnum.EXTRA;
  }

  public static getDaysForComponent(component: ApiComponent, packageStartDate: string): string {
    const startDate = dayjs(component.startDate);
    const endDate = dayjs(component.endDate);
    const componentDuration: number = endDate.diff(startDate, 'day');
    const packageStart = dayjs(packageStartDate);
    const startDay: number = startDate.diff(packageStart, 'day') + 1;
    return (
      startDay.toString() +
      (componentDuration > 0 ? `-${(startDay + componentDuration).toString()}` : '')
    );
  }

  public static getLowestPriceOfSelectable(component: ApiComponent): ApiPrice | undefined {
    let minPrice = component.selectableItems.find(
      item => !!PackageCustomizerUtil.getItemPrice(item)
    )?.price;

    component.selectableItems?.forEach(item => {
      const price = PackageCustomizerUtil.getItemPrice(item);
      if (price && minPrice && price.finalPrice < minPrice.finalPrice) {
        minPrice = price;
      }
    });

    return minPrice;
  }

  static getMediaForComponent(component: ApiComponent): ApiMedia | undefined {
    if (!component || !component.selectableItems || !component.selectableItems[0]) {
      return undefined;
    }

    const item = component.selectableItems[0];

    if (item.type === ItemTypesEnum.FLIGHT) {
      // @TODO add image to ApiFare
      return undefined;
    }
    if (item.type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).media;
    }
    if (item.type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).media;
    }
    if (item.type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).media;
    }
    if (item.type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).media;
    }
    if (item.type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).media;
    }
    return undefined;
  }

  static getImageForComponent(component: ApiComponent): ApiImage | undefined {
    const media = this.getMediaForComponent(component);
    if (media && media.mainImage && media.mainImage.small) {
      return media.mainImage.small;
    }
    if (media && media.mainImage && media.mainImage.offer) {
      return media.mainImage.offer;
    }
    if (media && media.mainImage && media.mainImage.extraSmall) {
      return media.mainImage.extraSmall;
    }
    if (media && media.mainImage && media.mainImage.large) {
      return media.mainImage.large;
    }
    if (media && media.mainImage && media.mainImage.extraLarge) {
      return media.mainImage.extraLarge;
    }
    return undefined;
  }

  static getStartItemDate(component: ApiComponent): string | undefined {
    if (!component || !component.selectableItems || !component.selectableItems[0]) {
      return undefined;
    }

    const item = component.selectableItems[0];

    if (item.type === ItemTypesEnum.FLIGHT) {
      const segment: ApiFlightSegment[] = (item as ApiFlightItem).segment || [];
      return segment.length > 0 ? segment[0].departure : undefined;
    }
    if (item.type === ItemTypesEnum.TRANSFER) {
      return (item as ApiTransfer).start;
    }
    if (item.type === ItemTypesEnum.HOTEL) {
      return (item as ApiHotel).start;
    }
    if (item.type === ItemTypesEnum.EXCURSION) {
      return (item as ApiExtraService).start;
    }
    if (item.type === ItemTypesEnum.INSURANCE) {
      return (item as ApiInsurance).start;
    }
    if (item.type === ItemTypesEnum.EXTRA) {
      return (item as ApiExtraService).start;
    }
    return undefined;
  }

  static getSelectedItems(packageDetails: ApiPackageDetails | null): ApiItem[] {
    const items: ApiItem[] = [];
    if (!packageDetails) {
      return items;
    }
    for (
      let indexComponent = 0;
      indexComponent < packageDetails.components.length;
      indexComponent += 1
    ) {
      const component = packageDetails.components[indexComponent];
      if (component) {
        for (let indexItems = 0; indexItems < component.selectedItems.length; indexItems += 1) {
          items.push(component.selectedItems[indexItems]);
        }
      }
    }
    return items;
  }

  static isFlightSelected(packageDetails: ApiPackageDetails | null): boolean {
    if (!packageDetails) {
      return false;
    }
    const flightComponents = packageDetails.components.filter(
      comp => comp.itemType === ApiItemType.FLIGHT
    );
    for (let indexComponent = 0; indexComponent < flightComponents.length; indexComponent += 1) {
      const component = flightComponents[indexComponent];
      if (component.selectedItems.length < 1 && component.relatedComponentId === undefined) {
        return false;
      }
      const relatedComponent = flightComponents.find(c => c.id === component.relatedComponentId);
      if (relatedComponent && relatedComponent.selectedItems.length < 1) {
        return false;
      }
    }
    return true;
  }

  static isAllRequiredAdded(packageDetails: ApiPackageDetails | null): boolean {
    if (!packageDetails) {
      return false;
    }
    for (
      let indexComponent = 0;
      indexComponent < packageDetails.components.length;
      indexComponent += 1
    ) {
      const component = packageDetails.components[indexComponent];
      if (
        component.componentType === ApiComponentType.REQUIRED ||
        component.componentType === ApiComponentType.MAIN
      ) {
        if (component.selectedItems.length < 1 && component.relatedComponentId === undefined) {
          return false;
        }
        const relatedComponent = packageDetails.components.find(
          c => c.id === component.relatedComponentId
        );
        if (relatedComponent && relatedComponent.selectedItems.length < 1) {
          return false;
        }
      }
    }
    return true;
  }

  static isComponentSelected(
    component: ApiComponent,
    allComponents: ApiComponent[]
  ): boolean | undefined {
    const relatedComponent = allComponents.find(c => c.id === component.relatedComponentId);
    return (
      component.selectedItems.length > 0 ||
      (relatedComponent && relatedComponent.selectedItems.length > 0)
    );
  }

  static getUnselectedOptionalComponents(components: ApiComponent[]): ApiComponent[] {
    const componentsToShow: ApiComponent[] = [];
    if (!components) {
      return componentsToShow;
    }
    for (let index = 0; index < components.length; index += 1) {
      if (
        components[index].componentType === ApiComponentType.OPTIONAL &&
        !PackageCustomizerUtil.isComponentSelected(components[index], components)
      ) {
        componentsToShow.push(components[index]);
      }
    }
    return componentsToShow;
  }

  static isFlightChosen(flightComponent: ApiComponent): boolean {
    return (
      flightComponent && flightComponent.selectedItems && flightComponent.selectedItems.length > 0
    );
  }

  static hasOptionalComponents(packageDetails: ApiPackageDetails | undefined): boolean {
    if (!packageDetails) {
      return false;
    }
    return packageDetails.components.some(
      component => component.componentType === ApiComponentType.OPTIONAL
    );
  }
}

export default PackageCustomizerUtil;
